import { Button } from '@1trading/react/components/ui/button';
import { GradientIcon } from '@1trading/react/components/ui/gradient-icon';
import { Loading } from '@1trading/react/components/ui/loading';
import { ChartCandlestick } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import type { BinaryAnswer } from '@/constants/answers';
import InstitutionLockedAnswersNotice from '@/features/institution/components/locked-answers-notice';
import { useInstitutionDetailsLocked } from '@/features/institution/hooks/useInstitutionDetails';
import type { InstitutionProductsOrDefaults } from '@/features/institution/products/schema';
import { usePromise } from '@/hooks/use-promise';

const InstitutionProductsFieldsetFuturesLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading className="space-y-8" centered asChild>
      <legend>
        <GradientIcon className="size-32 mx-auto" strokeWidth={1.5}>
          <ChartCandlestick />
        </GradientIcon>
        <div className="space-y-4">
          <PageHeadingTitle>{t('institutionDetails.products.futures.title')}</PageHeadingTitle>
          <PageHeadingDescription>{t('institutionDetails.products.futures.description')}</PageHeadingDescription>
        </div>
      </legend>
    </PageHeading>
  );
};
InstitutionProductsFieldsetFuturesLegend.displayName = 'InstitutionProductsFieldsetFuturesLegend';

const InstitutionProductsFieldsetFutures = () => {
  const form = useFormContext<InstitutionProductsOrDefaults>();
  const { nextStep } = useGuardedFormStepper<InstitutionProductsOrDefaults>();
  const { locked } = useInstitutionDetailsLocked();
  const { t } = useTranslation();
  const lockedFutures = locked.products?.futures;
  const chooseFutures = async (futures: BinaryAnswer) => {
    form.setValue('futures', futures);
    await nextStep();
  };
  const { isPending: isEnabling, handler: handleEnableFutures } = usePromise(() => chooseFutures('YES'));
  const { isPending: isSkipping, handler: handleSkipFutures } = usePromise(() => chooseFutures('NO'));
  const isPending = isEnabling || isSkipping;

  return (
    <div className="space-y-8">
      {lockedFutures ? <InstitutionLockedAnswersNotice /> : null}
      <footer className="flex flex-col gap-4">
        <Button className="relative" size="lg" variant="primary" disabled={isPending || lockedFutures === 'NO'} onClick={handleEnableFutures}>
          <span className={isEnabling ? 'invisible' : ''}>{t('institutionDetails.products.futures.enable')}</span>
          {isEnabling ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
        </Button>
        <Button className="relative" size="lg" variant="outline" disabled={isPending || lockedFutures === 'YES'} onClick={handleSkipFutures}>
          <span className={isSkipping ? 'invisible' : ''}>{t('institutionDetails.products.futures.skip')}</span>
          {isSkipping ? <Loading position="overlay" size="sm" /> : null}
        </Button>
        <p className="text-sm text-center text-muted-foreground">{t('institutionDetails.products.futures.skipDescription')}</p>
      </footer>
    </div>
  );
};
InstitutionProductsFieldsetFutures.displayName = 'InstitutionProductsFieldsetFutures';

export { InstitutionProductsFieldsetFutures, InstitutionProductsFieldsetFuturesLegend };
